import { ECategory } from '@common/enums/category.enum';
import { IRadioOption } from '@common/general-ui/radio-group/radio.component';
import { ISelectOption } from '@common/general-ui/select/select.component';
import { t } from '@common/hooks/use-t.hook';
import { ICollection } from '@common/interfaces/collection.interface';
import { UID } from '@common/interfaces/uid.interface';
import { dayjsEx } from '@common/utils/dayjs-ex.util';
import { formatPrice } from '@common/utils/format-price/format-price.util';
import { getCategoryTitle } from '@common/utils/get-category-title.util';
import { apiHooks } from '@src/api/hooks/api-hooks';
import { ICachedItemPriceDto } from '@src/api/model/cached-item-price.dto';
import { IDeliveryConfigDto } from '@src/api/model/delivery-config.dto';
import { IExclusionDto } from '@src/api/model/exclusion.dto';
import { IProductCatalogItemDto } from '@src/api/model/product-catalog-item.dto';
import { EDeliveryDay } from '@src/api/pack-house-generated-api';
import { IMarketingPreferences } from '@src/api/registration-generated-api';
import { ELayoutWidth } from '@src/shared/layout/layout.component';
import React, { useMemo } from 'react';
import { ICalendarEvent } from './calendar-event.interface';
import { ICheckboxOption } from '@common/general-ui/checkbox-group/checkbox-group.component';

const useGetQuantityOptions = (): ISelectOption[] => {
    const result = new Array(100).fill('').map((_val, index) => {
        return {
            label: index + 1 + '',
            value: index + 1,
        };
    });
    return result;
};

const useGetFrequencyOptions = (productId: UID, currentPriceId: string | undefined): ISelectOption[] => {
    const productsCollection: ICollection<IProductCatalogItemDto> | undefined = apiHooks.packHouse.getProductsFromCache();

    return useMemo(() => {
        const product = productsCollection?.list.find(
            (p: IProductCatalogItemDto) => p.id.toLowerCase() === productId.toLowerCase(),
        );
        const prices = product?.item?.prices;

        if (!prices || prices.length === 0) {
            return [];
        }

        return (
            prices?.filter((price) =>
                currentPriceId ? currentPriceId === price?.id || !price?.cf_hidden : !price?.cf_hidden,
            ) as ICachedItemPriceDto[]
        ).map((it: ICachedItemPriceDto): ISelectOption => {
            return {
                label: it.clientDescription || '',
                value: it.id || '',
            };
        });
    }, [productsCollection?.list, productId, currentPriceId]);
};

const useGetCategoryOptions = (): IRadioOption[] => {
    return useMemo(
        () =>
            Object.keys(ECategory).map((key) => {
                return {
                    value: key,
                    label: getCategoryTitle(key as ECategory),
                };
            }),
        [],
    );
};

// TODO: remove as unused?
export type TMarketingPreferences = 'yes' | 'no' | undefined;

const useGetNewsLetterOptions = (): IRadioOption[] => {
    return useMemo(() => {
        return [
            {
                label: t("Yes please, I'd love that"),
                value: 'yes',
            },
            {
                label: t('No thank you'),
                value: 'no',
            },
        ];
    }, []);
};

const useGetSkipDurationOptions = (): ISelectOption[] => {
    return useMemo(() => {
        return [
            {
                label: t('Skip a box'),
                value: 1,
            },
            {
                label: t('Skip 2 boxes'),
                value: 2,
            },
            {
                label: t('Skip 3 boxes'),
                value: 3,
            },
            {
                label: t('Skip 4 boxes'),
                value: 4,
            },
        ];
    }, []);
};

const useGetMarketingContentCheckboxes = (data: IMarketingPreferences | undefined): ICheckboxOption[] => {
    return useMemo(() => {
        return [
            {
                label: t('All the handy tips, exciting offers and other exclusive content'),
                value: !!data?.email_subscription_groups?.general_marketing,
            },
            {
                label: t('The Weekender – recipes and tips to help you make the most out of what’s in your box'),
                value: !!data?.email_subscription_groups?.weekly_newsletter,
            },
            {
                label: t('Add-ons – find out about last-minute rescues to add to your box'),
                value: !!data?.email_subscription_groups?.addons,
            },
            {
                label: t('Surveys – we’ll sometimes ask for your feedback to help make everything tip-top'),
                value: !!data?.email_subscription_groups?.surveys,
            },
            {
                label: t('Third party – we might share news and offers from our partners from time to time'),
                value: !!data?.email_subscription_groups?.['3rd_party'],
            },
        ];
    }, [data?.email_subscription_groups]);
};

const useGetMarketingChannelsCheckboxes = (data?: IMarketingPreferences | undefined): ICheckboxOption[] => {
    return useMemo(() => {
        return [
            {
                label: t('Email'),
                value: !!data?.email_subscribe,
            },
            {
                label: t('Post'),
                value: !!data?.postal_preference,
            },
            {
                label: t('Marketing SMS'),
                value: !!data?.sms_subscription_groups?.sms,
            },
            {
                label: t('Telephone'),
                value: !!data?.telephone_preference,
            },
        ];
    }, [data]);
};

const useGetStartDeliveryOptions = (
    data: ICalendarEvent[],
    { optionsCount = 4 }: { optionsCount?: number },
): IRadioOption[] => {
    return useMemo<IRadioOption[]>(() => {
        return data.slice(0, Math.min(data.length, optionsCount)).map((e: ICalendarEvent): IRadioOption => {
            if (e.metadata?.subscriptionObj) {
                return {
                    label: dayjsEx(e.metadata?.subscriptionObj?.subscriptionDeliveryDate).format('dddd, Do MMM') || '',
                    value: e.metadata?.subscriptionObj?.subscriptionDeliveryDate
                        ? dayjsEx(e.metadata?.subscriptionObj.subscriptionDeliveryDate).format('YYYY-MM-DD')
                        : '',
                    data: e,
                };
            } else {
                return {
                    label: dayjsEx(e.metadata?.priceObj?.subscriptionDeliveryDate).format('dddd, Do MMM') || '',
                    value: e.metadata?.priceObj?.subscriptionDeliveryDate
                        ? dayjsEx(e.metadata?.priceObj.subscriptionDeliveryDate).format('YYYY-MM-DD')
                        : '',
                    data: e,
                };
            }
        });
    }, [data, optionsCount]);
};

const useGetSwapBoxRenewalRadioOptions = (): IRadioOption[] => {
    return useMemo(() => {
        return [
            { label: t('Immediately'), value: 'false' },
            { label: t('On next renewal '), value: 'true' },
        ];
    }, []);
};

const useGetProductTypes = (): ISelectOption[] => {
    return useMemo(() => {
        return [
            { label: t('Box'), value: 'plan' },
            { label: t('Addon'), value: 'addon' },
        ];
    }, []);
};

const useGetLayoutWidthOptions = (): ISelectOption[] => {
    return useMemo(() => {
        return [
            { label: t('Normal'), value: ELayoutWidth.normal },
            { label: t('Large'), value: ELayoutWidth.large },
            { label: t('Extra Large'), value: ELayoutWidth.extraLarge },
        ];
    }, []);
};

const useGetDeliveryConfigOptions = (list: ICollection<IDeliveryConfigDto>): ISelectOption<IDeliveryConfigDto>[] => {
    return useMemo(
        () =>
            (list.list || []).map((config) => {
                return {
                    label: (
                        <div tw="whitespace-normal">
                            {config.name?.toString()} - {config.cutoff_day}d (
                            {formatPrice(config.delivery_costs?.every_other_week || 0)}) {config.delivery_day?.toUpperCase()}
                            '
                        </div>
                    ),
                    value: config.id,
                    data: config,
                };
            }),
        [list],
    );
};

const useGetDeliveryDayOptions = (): ISelectOption[] => {
    return useMemo(() => {
        return [
            { label: getValueFromDayOfWeekEnum(EDeliveryDay.Mon), value: EDeliveryDay.Mon },
            { label: getValueFromDayOfWeekEnum(EDeliveryDay.Tue), value: EDeliveryDay.Tue },
            { label: getValueFromDayOfWeekEnum(EDeliveryDay.Wed), value: EDeliveryDay.Wed },
            { label: getValueFromDayOfWeekEnum(EDeliveryDay.Thu), value: EDeliveryDay.Thu },
            { label: getValueFromDayOfWeekEnum(EDeliveryDay.Fri), value: EDeliveryDay.Fri },
            { label: getValueFromDayOfWeekEnum(EDeliveryDay.Sat), value: EDeliveryDay.Sat },
            { label: getValueFromDayOfWeekEnum(EDeliveryDay.Sun), value: EDeliveryDay.Sun },
        ];
    }, []);
};

const map: Record<EDeliveryDay, string> = {
    Mon: t('Monday'),
    Tue: t('Tuesday'),
    Wed: t('Wednesday'),
    Thu: t('Thursday'),
    Fri: t('Friday'),
    Sat: t('Saturday'),
    Sun: t('Sunday'),
};
export const getValueFromDayOfWeekEnum = (dayOfWeek: EDeliveryDay | undefined): string => {
    if (!dayOfWeek) {
        return '';
    }
    return map[dayOfWeek];
};

const isGroupExclusion = (code: string): boolean => code.toLowerCase().indexOf('g-') === 0;
const useGetAllergiesOptions = (data: ICollection<IExclusionDto> | undefined): ISelectOption[] => {
    return useMemo(() => {
        const groupOptions = (data?.list || [])
            .filter((f) => isGroupExclusion(f.product_code || ''))
            .map(
                (it): ISelectOption => ({
                    label: it.label || '',
                    value: it.id || '',
                }),
            );
        const options = (data?.list || [])
            .filter((f) => !isGroupExclusion(f.product_code || ''))
            .sort((a, b) => {
                if (a.label > b.label) {
                    return 1;
                }
                if (a.label < b.label) {
                    return -1;
                }
                return 0;
            })
            .map(
                (it): ISelectOption => ({
                    label: it.label || '',
                    value: it.id || '',
                }),
            );
        return groupOptions.length && options.length
            ? [...options, { label: t('divider'), value: 'divider', data: 'divider' } as ISelectOption, ...groupOptions]
            : [...options, ...groupOptions];
    }, [data]);
};

export const dictionary = {
    useGetSwapBoxRenewalRadioOptions,
    useGetAllergiesOptions,
    useGetMarketingChannelsCheckboxes,
    useGetMarketingContentCheckboxes,
    useGetLayoutWidthOptions,
    useGetDeliveryConfigOptions,
    useGetQuantityOptions,
    useGetFrequencyOptions,
    useGetStartDeliveryOptions,
    useGetNewsLetterOptions,
    useGetSkipDurationOptions,
    useGetProductTypes,
    useGetCategoryOptions,
    useGetDeliveryDayOptions,
};
